import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Link2, MessageSquare, Settings as SettingsIcon, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/articles/new", label: "New Article", icon: FileText, match: "/admin/articles" },
  { to: "/admin/affiliates", label: "Affiliate Links", icon: Link2 },
  { to: "/admin/feedback", label: "Reader Feedback", icon: MessageSquare },
  { to: "/admin/settings", label: "Scoring Settings", icon: SettingsIcon },
];

export function AdminShell({ title, actions, children }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const isActive = (n) => n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.match || n.to);

  return (
    <div className="min-h-screen bg-[#F7F7F5] flex">
      <aside className="w-60 bg-stone-900 text-stone-300 flex-col hidden md:flex sticky top-0 h-screen">
        <div className="p-6 border-b border-stone-700">
          <div className="font-serif-display text-xl font-black text-white">The PM Test</div>
          <div className="font-mono-pm text-[9px] tracking-[0.2em] uppercase text-[#CCFF00] mt-1">Admin</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`flex items-center gap-3 px-3 py-2.5 font-mono-pm text-xs tracking-wide uppercase transition-colors ${isActive(n) ? "bg-[#CCFF00] text-stone-900" : "text-stone-400 hover:text-white hover:bg-stone-800"}`}>
              <n.icon size={16} /> {n.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-700 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 font-mono-pm text-xs uppercase text-stone-400 hover:text-white">
            <ExternalLink size={16} /> View Site
          </a>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 font-mono-pm text-xs uppercase text-stone-400 hover:text-white">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b-2 border-stone-900 sticky top-0 z-30">
          <div className="px-4 sm:px-8 py-5 flex items-center justify-between gap-4">
            <h1 className="font-serif-display text-2xl sm:text-3xl font-black text-stone-900 truncate">{title}</h1>
            <div className="flex items-center gap-3 shrink-0">{actions}</div>
          </div>
          {/* mobile nav */}
          <div className="md:hidden border-t border-stone-200 px-2 py-2 flex gap-1 overflow-x-auto">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`shrink-0 font-mono-pm text-[10px] uppercase px-3 py-2 ${isActive(n) ? "bg-stone-900 text-white" : "text-stone-500"}`}>{n.label}</Link>
            ))}
            <button onClick={() => { logout(); nav("/admin/login"); }} className="shrink-0 font-mono-pm text-[10px] uppercase px-3 py-2 text-[#991B1B]">Logout</button>
          </div>
        </header>
        <main className="p-4 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
